import { Visibility } from '@mui/icons-material';
import { Box, Paper, Stack } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { EActiveDrawer, EInteractionMode, ESubMenu, EToolbarButton, EWallMode } from '../../../@types/Enums';
import { IconBehavior, IconGuide, IconMeasure } from '../../../assets/icons';
import useResponsiveSizes from '../../../hooks/useResponsiveSizes';
import { useAnnotations } from '../../../provider/AnnotationsProvider';
import { useDraggingContext } from '../../../provider/DraggingProvider';
import { useDrawing } from '../../../provider/DrawingProvider';
import { useMultiSelectToggleContext } from '../../../provider/MultiSelectToggleProvider';
import { useSelection } from '../../../provider/SelectionProvider';
import { useSingleSelectContext } from '../../../provider/SingleSelectProvider';
import { useToolbarContext } from '../../../provider/ToolbarProvider';
import { primaryIconActive, primaryIconInactive } from '../../../utils/colors';
import { LONG_PRESS_TIME_TOOLBAR } from '../../../utils/constants';
import ImsIcon from '../ImsIcon';
import BehaviorButton from './buttons/BehaviorButton';
import ExtendButton from './buttons/ExtendButton';
import HistoryButton from './buttons/HistoryButton';
import MeasureButton from './buttons/MeasureButton';
import RoomCreationButton from './buttons/RoomCreationButton';
import SelectModeButton from './buttons/SelectModeButton';
import VisibilityButton from './buttons/VisibilityButton';
import ToolbarButton from './ToolbarButton';

const Toolbar = () => {
  const singleSelectParentRef = useRef<HTMLButtonElement>(null);
  const touchTimerRef = useRef<NodeJS.Timeout>();
  const historyRef = useRef<HTMLButtonElement>(null);
  const roomCreationRef = useRef<HTMLButtonElement>(null);
  const extendRef = useRef<HTMLButtonElement>(null);
  const behaviorRef = useRef<HTMLButtonElement>(null);
  const visibilityRef = useRef<HTMLButtonElement>(null);

  const [buttonPosition, setButtonPosition] = React.useState({ top: 0, left: 0 });
  const [selectModePosition, setSelectModePosition] = React.useState<DOMRect>();

  const isLongPress = useRef<boolean>(false);
  const sizes = useResponsiveSizes();
  const { currentlySelected, setCurrentlySelected, items } = useSingleSelectContext();
  const { activeDrawer, setActiveDrawer } = useSelection();
  const { roomCompleted, setRoomCompleted } = useDrawing();
  const intl = useIntl();
  const { currentlyActiveSubMenu, setCurrentlyActiveSubMenu, interactionMode, setInteractionMode, wallMode, setWallMode, isDrawingAllowed, setIsDrawingAllowed } =
    useToolbarContext();
  const { isDragging } = useDraggingContext();
  const { addHotspot } = useAnnotations();
  const { showHotspots, setShowHotspots } = useMultiSelectToggleContext();
  const [activeToolbarButton, setActiveToolbarButton] = useState<EToolbarButton | null>(null);

  const handleButtonPositionUpdate = React.useCallback((rect: DOMRect) => {
    setButtonPosition(rect);
  }, []);

  // Reset modes to default (e.g. select mode)
  const resetModes = () => {
    if (activeDrawer !== EActiveDrawer.NONE) return;

    setWallMode(EWallMode.NONE);
    setInteractionMode(EInteractionMode.NONE);
    const defaultItem = items.find((item) => item.id === 0) || items[0];
    if (currentlySelected.id !== defaultItem.id) {
      setCurrentlySelected(defaultItem);
    }
  };

  useEffect(() => {
    if (currentlySelected.id === 1) {
      if (interactionMode !== EInteractionMode.MOVE) {
        setInteractionMode(EInteractionMode.MOVE);
      }
    } else if (currentlySelected.id === 2) {
      if (interactionMode !== EInteractionMode.COMMENT) {
        setInteractionMode(EInteractionMode.COMMENT);
      }
      if (!showHotspots) {
        setShowHotspots(true);
      }
    } else {
      if (
        interactionMode !== EInteractionMode.NONE &&
        interactionMode !== EInteractionMode.MEASURE &&
        interactionMode !== EInteractionMode.ATTACHMENT &&
        interactionMode !== EInteractionMode.SET_TEXTURE &&
        interactionMode !== EInteractionMode.DRAW
      ) {
        setInteractionMode(EInteractionMode.NONE);
      }
    }
  }, [currentlySelected.id, interactionMode, showHotspots, setInteractionMode, setShowHotspots]);

  // Handle all toolbar button presses
  const handleToolbarButtonPress = (buttonId: EToolbarButton, subMenuType: ESubMenu = ESubMenu.NONE, activatedDrawer: EActiveDrawer = EActiveDrawer.NONE) => {
    setWallMode(EWallMode.NONE);

    if (buttonId === EToolbarButton.DRAWING) {
      if (interactionMode === EInteractionMode.DRAW) {
        // Turn OFF
        setInteractionMode(EInteractionMode.NONE);
        setIsDrawingAllowed(false);
        setActiveToolbarButton(null);
      } else {
        // Turn ON
        resetModes(); 
        setRoomCompleted(false);
        setInteractionMode(EInteractionMode.DRAW);
        setIsDrawingAllowed(true); // MUST BE TRUE
        setActiveToolbarButton(EToolbarButton.ROOM_CREATION); // Highlight the Parent (Pencil)
      }
      // CRITICAL: Exit here so we don't overwrite the active button at the bottom
      return; 
    
    }
    // Handle MEASURE
    else if (buttonId === EToolbarButton.MEASURE) {
      resetModes();
      setInteractionMode(EInteractionMode.MEASURE);
      // Ensure drawing is off if we switch to measure
      setIsDrawingAllowed(false);
    }
    // Handle ATTACHMENT
    else if (buttonId === EToolbarButton.ATTACHMENT) {
      resetModes();
      addHotspot(null);
      // Ensure drawing is off
      setIsDrawingAllowed(false);
    }
    // Handle MODE (Select) or others
    else if (buttonId !== EToolbarButton.MODE) {
      if (interactionMode !== EInteractionMode.NONE) {
        resetModes();
      }
      // If switching to any other tool, kill drawing
      setIsDrawingAllowed(false);
    }

    // Toggle the active drawer
    if (activeDrawer !== activatedDrawer) {
      setActiveDrawer(activatedDrawer);
    } else {
      setActiveDrawer(EActiveDrawer.NONE);
    }
    // Toggle the submenu
    if (currentlyActiveSubMenu !== subMenuType) {
      setCurrentlyActiveSubMenu(subMenuType);
    } else {
      setCurrentlyActiveSubMenu(ESubMenu.NONE);
    }
    setActiveToolbarButton(buttonId);
  };

  // Long press touch logic
  const startPressTimer = () => {
    isLongPress.current = false;
    touchTimerRef.current = setTimeout(() => {
      isLongPress.current = true;
      handleToolbarButtonPress(0, ESubMenu.NONE);
    }, LONG_PRESS_TIME_TOOLBAR);
  };

  const handleInputDown = () => {
    startPressTimer();
  };

  const handleInputUp = () => {
    clearTimeout(touchTimerRef.current);
  };

  // Utility: Returns the icon for the single select button.
  const getSingleSelectDisplayIcon = (): JSX.Element => <ImsIcon Icon={currentlySelected.icon as React.FunctionComponent<any>} />;

  return (
    <Box
      component="div"
      className="parent"
      sx={{
        position: 'relative',
        top: '6rem',
        userSelect: isDragging ? 'none' : 'auto',
        pointerEvents: isDragging ? 'none' : 'auto',
      }}
    >
      <Paper
        elevation={3}
        sx={{
          position: 'absolute',
          left: 16,
          zIndex: 1000,
          isolation: 'isolate',
          backgroundColor: 'white',
          borderRadius: '100px',
          p: sizes.toolbarPadding,
          top: 'calc(50vh - 5.6rem)',
          transform: 'translateY(-50%)',
        }}
      >
        <Stack
          direction="column"
          spacing={sizes.toolbarButtonSpacing}
          sx={{
            position: 'relative',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {/* 1. Select / SingleSelect Mode Button */}
          <SelectModeButton
            onPositionUpdate={handleButtonPositionUpdate}
            tooltip={currentlySelected.text}
            icon={getSingleSelectDisplayIcon()}
            onClick={() => handleToolbarButtonPress(EToolbarButton.MODE, ESubMenu.MODE)}
            onMouseDown={handleInputDown}
            onMouseUp={handleInputUp}
            onTouchStart={handleInputDown}
            onTouchEnd={handleInputUp}
            ref={singleSelectParentRef}
            active={true}
            sizes={sizes}
          />

          {/* 2. History: Undo | Redo | History(Drawer) */}
          <HistoryButton
            ref={historyRef}
            sizes={sizes}
            handleToolbarButtonPress={handleToolbarButtonPress}
            activeDrawer={activeDrawer}
            activeToolbarButton={activeToolbarButton}
            setActiveToolbarButton={setActiveToolbarButton}
            onPositionUpdate={handleButtonPositionUpdate}
          />

          {/* 3. Contains Wall Drawing (Room Drawing) and Architecure */}
          <RoomCreationButton
            ref={roomCreationRef}
            sizes={sizes}
            handleToolbarButtonPress={handleToolbarButtonPress}
            isDrawingAllowed={isDrawingAllowed}
            activeDrawer={activeDrawer}
            activeToolbarButton={activeToolbarButton}
            setActiveToolbarButton={setActiveToolbarButton}
            onPositionUpdate={handleButtonPositionUpdate}
          />

          {/* 4. Measure Button */}
          <MeasureButton
            tooltip={intl.formatMessage({ id: 'Toolbar.measure' })}
            icon={<ImsIcon Icon={IconMeasure} />}
            active={interactionMode === EInteractionMode.MEASURE}
            sizes={sizes}
            handleToolbarButtonPress={handleToolbarButtonPress}
          />
          {/* 5. Extend Button */}
          <ExtendButton
            ref={extendRef}
            sizes={sizes}
            handleToolbarButtonPress={handleToolbarButtonPress}
            activeDrawer={activeDrawer}
            activeToolbarButton={activeToolbarButton}
            setActiveToolbarButton={setActiveToolbarButton}
            onPositionUpdate={handleButtonPositionUpdate}
          />

          {/* 6. Visibility Button with its Modal */}
          <VisibilityButton
            buttonRef={visibilityRef}
            tooltip={intl.formatMessage({ id: 'Toolbar.visibility' })}
            onClick={() => handleToolbarButtonPress(EToolbarButton.VISIBILITY, ESubMenu.VISIBILITY)}
            icon={<Visibility />}
            active={currentlyActiveSubMenu === ESubMenu.VISIBILITY}
            sizes={sizes}
            onPositionUpdate={handleButtonPositionUpdate}
          />

          {/* 7. Behavior Button with its Modal */}
          <BehaviorButton
            ref={behaviorRef}
            tooltip={intl.formatMessage({ id: 'Toolbar.behavior' })}
            onClick={() => handleToolbarButtonPress(EToolbarButton.BEHAVIOR, ESubMenu.BEHAVIOR)}
            icon={<ImsIcon Icon={IconBehavior} fill={currentlyActiveSubMenu === ESubMenu.BEHAVIOR ? primaryIconActive : primaryIconInactive} />}
            active={currentlyActiveSubMenu === ESubMenu.BEHAVIOR}
            sizes={sizes}
            onPositionUpdate={handleButtonPositionUpdate}
          />
          {/* 8. Guide / Help Button – opens a link */}
<ToolbarButton
  tooltip={intl.formatMessage({ id: 'Toolbar.guide', defaultMessage: 'Anleitung öffnen' })}
  icon={<ImsIcon Icon={IconGuide} />}
  sizes={sizes}
  onClick={() => {

    window.open('https://immersight.com/grundrissplaner_anleitung.pdf', '_blank', 'noreferrer');
  }}
/>

        </Stack>
      </Paper>
    </Box>
  );
};

export default Toolbar;
